import { useEffect, useRef, useState, useContext } from "react";
import { AuthContext } from "../../../contexts/authContext";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Modal, Menu, ActionIcon, Textarea } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconDotsVertical } from '@tabler/icons-react';

import AppHdWithReturnLeft from "../../../composents/Headers/AppHeaders/AppHdWithReturnLeft/AppHdWithReturnLeft";

// Contenu complet d'une conversation (header + messages + CTA + input). Extrait de
// Chatpage.js pour être réutilisable : Chatpage.js l'affiche seul (route /app/msg/chat/:id,
// utilisée par navigation directe en mobile et desktop), et List.js l'affiche à droite
// de la liste des conversations sur desktop, sans changer de route.
const ChatPanel = ({ conversationId, hideHeaderBack = false, onBack = null }) => {
  const { getToken } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const qp = new URLSearchParams(location.search);
  const preselectedStart = qp.get("start");
  const preselectedEnd = qp.get("end");

  const [userId, setUserId] = useState("");
  const [recipientId, setRecipientId] = useState(null);
  const [offer, setOffer] = useState(null);
  const [convStartDate, setConvStartDate] = useState(null);
  const [convEndDate, setConvEndDate] = useState(null);
  const [conversationTransaction, setConversationTransaction] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [cancelModalOpened, { open: openCancelModal, close: closeCancelModal }] = useDisclosure(false);
  const [cancelTransactionId, setCancelTransactionId] = useState(null);
  const [cancelIsPostValidation, setCancelIsPostValidation] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);
  const [refuseModalOpened, { open: openRefuseModal, close: closeRefuseModal }] = useDisclosure(false);
  const [refuseMessageId, setRefuseMessageId] = useState(null);
  const [refuseReason, setRefuseReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [reservationLoading, setReservationLoading] = useState(false);

  const messagesRef = useRef(null);

  // -------------------------
  // FETCH CONVERSATION
  // -------------------------
  const refetchConversation = async (currentUserId) => {
    const token = getToken();
    if (!token) return;

    try {
      const res = await fetch(
        `https://boldee-97d8d36af3bd.herokuapp.com/app/msg/conversations/${conversationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) return;
      const data = await res.json();

      setOffer(data.offer || null);
      setConvStartDate(data.startDate || null);
      setConvEndDate(data.endDate || null);
      setConversationTransaction(data.transaction || null);
      setMessages(Array.isArray(data.messages) ? data.messages : []);

      if (currentUserId) {
        const other = (data.participants || []).find(
          p => p.toString() !== currentUserId.toString()
        );
        setRecipientId(other?.toString() || null);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const fetchConversation = async () => {
      const token = getToken();

      if (!token) {
        navigate("/app/login");
        return;
      }

      try {
        const userRes = await fetch(
          "https://boldee-97d8d36af3bd.herokuapp.com/app/getUser",
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!userRes.ok) { navigate("/app/login"); return; }
        const userData = await userRes.json();
        const currentUserId = userData._id;
        setUserId(currentUserId);

        await refetchConversation(currentUserId);
      } catch (err) {
        console.error(err);
      }
    };

    if (conversationId) fetchConversation();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, getToken, navigate]);

  // -------------------------
  // MARK AS READ
  // -------------------------
  useEffect(() => {
    const markAsRead = async () => {
      const token = getToken();
      if (!token) return;
      try {
        await fetch(
          `https://boldee-97d8d36af3bd.herokuapp.com/app/msg/conversations/${conversationId}/read`,
          {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      } catch (err) {
        console.error(err);
      }
    };

    if (conversationId) markAsRead();

  }, [conversationId, getToken]);

  // -------------------------
  // SCROLL
  // -------------------------
  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop =
        messagesRef.current.scrollHeight;
    }
  }, [messages]);

  // -------------------------
  // SEND MESSAGE
  // -------------------------
  const sendMessage = async () => {
    if (!text.trim() || !offer?._id) return;

    const token = getToken();
    try {
      const res = await fetch(
        "https://boldee-97d8d36af3bd.herokuapp.com/app/msg/send",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            offerId: offer._id,
            to: recipientId,
            content: text,
            conversationId
          })
        }
      );
      if (!res.ok) return;
      const newMessage = await res.json();
      setMessages(prev => [...prev, newMessage]);
      setText("");
    } catch (err) {
      console.error(err);
    }
  };

  // -------------------------
  // ACTIONS
  // -------------------------
const acceptBooking = async (messageId) => {
  if (actionLoading) return;
  setActionLoading(true);
  const token = getToken();

  try {
    const res = await fetch(
      `https://boldee-97d8d36af3bd.herokuapp.com/app/msg/${messageId}/accept`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      }
    );

    const data = await res.json();

    setMessages(prev => {
      const safePrev = Array.isArray(prev) ? prev : [];
      const updated = safePrev.map(m =>
        m._id === messageId ? { ...m, actionStatus: "accepted" } : m
      );
      const newMsgs = [data.sellerMessage, data.buyerMessage].filter(Boolean);
      return [...updated, ...newMsgs];
    });
    await refetchConversation(userId);
  } finally {
    setActionLoading(false);
  }
};

  const refuseBooking = async (messageId, reason) => {
    if (actionLoading) return;
    setActionLoading(true);
    const token = getToken();

    try {
      const res = await fetch(
        `https://boldee-97d8d36af3bd.herokuapp.com/app/msg/${messageId}/refuse`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ reason })
        }
      );

      const data = await res.json();

      if (!res.ok) {
        alert(data.message || "Une erreur est survenue.");
        return;
      }

      setMessages(prev => {
        const safePrev = Array.isArray(prev) ? prev : [];
        const updated = safePrev.map(m =>
          m._id === messageId ? { ...m, actionStatus: "refused" } : m
        );
        const newMsgs = [data.sellerMessage, data.buyerMessage].filter(Boolean);
        return [...updated, ...newMsgs];
      });
      await refetchConversation(userId);
    } finally {
      setActionLoading(false);
    }
  };

  const confirmRefuseBooking = async () => {
    if (!refuseReason.trim()) return;
    const messageId = refuseMessageId;
    const reason = refuseReason.trim();
    closeRefuseModal();
    setRefuseMessageId(null);
    setRefuseReason("");
    if (messageId) await refuseBooking(messageId, reason);
  };

  const confirmDelivery = async (messageId, transactionId) => {
    if (actionLoading) return;
    setActionLoading(true);
    const token = getToken();

    try {
      const res = await fetch(
        `https://boldee-97d8d36af3bd.herokuapp.com/app/payment/transaction/${transactionId}/release`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (!res.ok) {
        alert("Une erreur est survenue. Veuillez réessayer.");
        return;
      }

      const data = await res.json();

      setMessages(prev => {
        const updated = prev.map(m =>
          m._id === messageId ? { ...m, actionStatus: "accepted" } : m
        );
        return data.buyerMessage ? [...updated, data.buyerMessage] : updated;
      });
      await refetchConversation(userId);
    } finally {
      setActionLoading(false);
    }
  };


  const cancelTransaction = async () => {
    if (!cancelReason.trim() || cancelLoading) return;
    setCancelLoading(true);

    try {
      const token = getToken();

      const res = await fetch(
        `https://boldee-97d8d36af3bd.herokuapp.com/app/payment/transaction/${cancelTransactionId}/cancel`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ reason: cancelReason.trim() })
        }
      );

      closeCancelModal();
      setCancelReason("");

      if (!res.ok) {
        const err = await res.json();
        alert(err.error || "Une erreur est survenue.");
        return;
      }

      setMessages(prev => [...prev, {
        _id: Date.now().toString(),
        type: "system",
        content: "La réservation a été annulée.",
        createdAt: new Date().toISOString()
      }]);
      await refetchConversation(userId);
    } finally {
      setCancelLoading(false);
    }
  };

  // -------------------------
  // ROLES
  // -------------------------
  const isSeller = () =>
    offer?.author?._id?.toString() === userId?.toString();

  const hasActiveTransaction = () => {
    const activeStatuses = ["funds_secured", "validated", "to_released", "released"];
    return messages.some(m => m.transaction && activeStatuses.includes(m.transaction.status));
  };

  // Ne regarde que les messages liés à la transaction en cours (conversationTransaction) :
  // une conversation peut avoir plusieurs transactions dans le temps (ex: un refus suivi
  // d'un nouvel achat sur la même offre une fois redevenue disponible), et un refus d'une
  // transaction passée ne doit pas bloquer la transaction actuelle.
  const wasRefused = () => {
    if (!conversationTransaction?._id) return false;
    const currentTxId = conversationTransaction._id.toString();
    return messages.some(m =>
      m.actionStatus === "refused" &&
      (m.transaction?._id || m.transaction)?.toString() === currentTxId
    );
  };

  const wasLocationRefusedOrCancelled = () =>
    offer?.contrat === "Location" &&
    (wasRefused() || ["cancelled_seller", "cancelled_buyer"].includes(conversationTransaction?.status));

  const isOfferNoLongerAvailable = () => {
    if (offer?.contrat !== "Vente") return false;
    const terminalStatuses = ["refunded", "cancelled_buyer", "cancelled_seller"];
    const wasCancelledOrRefused = terminalStatuses.includes(conversationTransaction?.status) || wasRefused();
    return wasCancelledOrRefused && offer?.status !== "open";
  };

  const canCancelConversation = () => {
    if (!conversationTransaction) return false;
    const txStatus = conversationTransaction.status;
    const isLocation = offer?.contrat === "Location";
    const within72h = conversationTransaction.createdAt &&
      new Date() < new Date(new Date(conversationTransaction.createdAt).getTime() + 72 * 60 * 60 * 1000);
    // Le vendeur a encore une décision Accepter/Refuser en attente sur cette demande :
    // pas d'option "Annuler" tant que ce choix n'a pas été fait (cf. le bloc Accepter/Refuser
    // plus bas, qui s'affiche dans exactement les mêmes conditions : actionStatus "pending"
    // + transaction "funds_secured").
    const hasPendingDecision = messages.some(m => m.actionStatus === "pending") && txStatus === "funds_secured";
    // Une fois la transaction "validated" (le vendeur a accepté), seul le vendeur peut
    // encore annuler — règle appliquée côté backend aussi (POST .../cancel renvoie 403
    // à l'acheteur sur ce statut). Avant validation, l'acheteur garde la main (72h pour
    // une Location, sans limite pour Vente/Don).
    const buyerCanCancel = txStatus === "funds_secured" && (!isLocation || within72h);
    const sellerCanCancel = !hasPendingDecision && (txStatus === "funds_secured" || txStatus === "validated");
    return isSeller() ? sellerCanCancel : buyerCanCancel;
  };

  const ctaLabel = () => {
    if (wasLocationRefusedOrCancelled()) return "Vérifier d'autres disponibilités";
    if (offer?.contrat === "Location") return "Réserver";
    if (offer?.contrat === "Don") return "Solliciter le don";
    return "Acheter";
  };

  return (
    <div className="chat-panel-root">
      <Modal size="auto" opened={cancelModalOpened} onClose={() => { closeCancelModal(); setCancelReason(""); }} centered title="Confirmer l'annulation">
        {cancelIsPostValidation && (
          <p style={{ color: "#868e96", textAlign: "center" }}>
          Cette réservation a déjà été acceptée.
          </p>
        )}
        <p style={{ textAlign: "center" }}>{offer?.contrat === "Don" ? "Êtes-vous sûr de vouloir annuler cette donation ?" : "Êtes-vous sûr de vouloir annuler cette réservation ?"}</p>
        {!cancelIsPostValidation && offer?.contrat !== "Don" && (
          <p style={{ color: "#27ae60", textAlign: "center" }}>Vous serez remboursé intégralement.</p>
        )}
        <Textarea
          label="Raison de l'annulation"
          placeholder="Expliquez pourquoi vous annulez..."
          value={cancelReason}
          onChange={(e) => setCancelReason(e.currentTarget.value)}
          minRows={2}
          mt="sm"
          required
          withAsterisk
        />
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <Button fullWidth variant="outline" color="dark" disabled={cancelLoading} onClick={() => { closeCancelModal(); setCancelReason(""); }}>Retour</Button>
          <Button fullWidth variant="outline" color="dark" disabled={!cancelReason.trim()} loading={cancelLoading} onClick={cancelTransaction}>Confirmer l'annulation</Button>
        </div>
      </Modal>

      <Modal size="auto" opened={refuseModalOpened} onClose={() => { closeRefuseModal(); setRefuseMessageId(null); setRefuseReason(""); }} centered title="Confirmer votre refus">
        <p style={{ textAlign: "center" }}>
          {offer?.contrat === "Don"
            ? "Êtes-vous sûr de vouloir refuser cette demande de don ?"
            : offer?.contrat === "Location"
            ? "Êtes-vous sûr de vouloir refuser cette réservation de location ?"
            : "Êtes-vous sûr de vouloir refuser cette vente ?"}
        </p>
        <Textarea
          label="Raison du refus"
          placeholder="Expliquez pourquoi vous refusez..."
          value={refuseReason}
          onChange={(e) => setRefuseReason(e.currentTarget.value)}
          minRows={2}
          mt="sm"
          required
          withAsterisk
        />
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <Button fullWidth variant="outline" color="dark" onClick={() => { closeRefuseModal(); setRefuseMessageId(null); setRefuseReason(""); }}>Retour</Button>
          <Button fullWidth variant="outline" color="dark" disabled={!refuseReason.trim()} loading={actionLoading} onClick={confirmRefuseBooking}>Confirmer le refus</Button>
        </div>
      </Modal>

      {!hideHeaderBack ? (
        <AppHdWithReturnLeft
          rightContent={
            canCancelConversation() && (
              <Menu shadow="md" width={200} position="bottom-end">
                <Menu.Target>
                  <ActionIcon variant="transparent" color="dark">
                    <IconDotsVertical size={22} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    onClick={() => {
                      setCancelTransactionId(conversationTransaction._id);
                      setCancelIsPostValidation(conversationTransaction.status === "validated");
                      openCancelModal();
                    }}
                  >
                    Annuler
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )
          }
        >
          <p className="noMrgTB bold">{offer?.title}</p>
          <p className="noMrgTB medium">{offer?.author?.company?.name}</p>
          {offer?.contrat === "Location" && (() => {
            const tx = messages.find(m => m.transaction?.startDate && m.transaction?.endDate)?.transaction;
            const fmt = (d) => new Date(d).toLocaleDateString("fr-FR", { timeZone: "Europe/Paris" });
            const d1 = tx?.startDate || convStartDate || preselectedStart;
            const d2 = tx?.endDate   || convEndDate   || preselectedEnd;
            if (!d1 || !d2) return null;
            return <p className="noMrgTB medium">{fmt(d1)} — {fmt(d2)}</p>;
          })()}
        </AppHdWithReturnLeft>
      ) : (
        <div className="chat-panel-header">
          <div>
            <p className="noMrgTB bold">{offer?.title}</p>
            <p className="noMrgTB medium">{offer?.author?.company?.name}</p>
            {offer?.contrat === "Location" && (() => {
              const tx = messages.find(m => m.transaction?.startDate && m.transaction?.endDate)?.transaction;
              const fmt = (d) => new Date(d).toLocaleDateString("fr-FR", { timeZone: "Europe/Paris" });
              const d1 = tx?.startDate || convStartDate || preselectedStart;
              const d2 = tx?.endDate   || convEndDate   || preselectedEnd;
              if (!d1 || !d2) return null;
              return <p className="noMrgTB medium">{fmt(d1)} — {fmt(d2)}</p>;
            })()}
          </div>
          {canCancelConversation() && (
            <Menu shadow="md" width={200} position="bottom-end">
              <Menu.Target>
                <ActionIcon variant="transparent" color="dark">
                  <IconDotsVertical size={22} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  onClick={() => {
                    setCancelTransactionId(conversationTransaction._id);
                    setCancelIsPostValidation(conversationTransaction.status === "validated");
                    openCancelModal();
                  }}
                >
                  Annuler
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          )}
        </div>
      )}
    <div className="chat-container">
    <div className="messages" ref={messagesRef}>
      {messages
        .filter(msg => {
          const toId = (msg.to?._id || msg.to)?.toString();
          const senderId = (msg.from?._id || msg.from)?.toString();

          return (
            !msg.to || // message global
            toId === userId?.toString() || // message pour moi
            senderId === userId?.toString() // message envoyé par moi
          );
        })
        .map((msg, idx, arr) => {

          const senderId = (msg.from?._id || msg.from)?.toString();
          const isMine = senderId === userId?.toString();

          // Séparateur de date façon WhatsApp : affiché avant le premier message du jour,
          // "Aujourd'hui" / "Hier" / date complète selon l'ancienneté.
          const msgDay = new Date(msg.createdAt).toDateString();
          const prevMsgDay = idx > 0 ? new Date(arr[idx - 1].createdAt).toDateString() : null;
          const showDateSeparator = msgDay !== prevMsgDay;

          const dateSeparatorLabel = (() => {
            const msgDate = new Date(msg.createdAt);
            const today = new Date();
            const yesterday = new Date();
            yesterday.setDate(today.getDate() - 1);
            if (msgDay === today.toDateString()) return "Aujourd'hui";
            if (msgDay === yesterday.toDateString()) return "Hier";
            return msgDate.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
          })();

          const dateSeparator = showDateSeparator && (
            <div key={`sep-${msg._id}`} className="chat-date-separator">
              <span>{dateSeparatorLabel}</span>
            </div>
          );

          if (msg.type === "system") {
            const transactionId = msg.transaction?._id || msg.transaction;
            // Une conversation peut accumuler plusieurs transactions dans le temps (ex: une
            // première demande jamais traitée, expirée après 72h, suivie d'une nouvelle
            // demande sur les mêmes dates). Les boutons Accepter/Refuser ne doivent agir que
            // sur le message "pending" de la transaction actuellement en cours, pas sur un
            // vieux message "pending" orphelin d'une transaction passée — sinon les deux
            // jeux de boutons s'affichent en même temps.
            const belongsToCurrentTransaction =
              conversationTransaction?._id &&
              transactionId?.toString() === conversationTransaction._id.toString();

            return (
              <div key={msg._id}>
                {dateSeparator}
                <div className="vertical system-message">

                <span className="thin txt11">{new Date(msg.createdAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
                <span>{msg.content}</span>

                {isSeller() && belongsToCurrentTransaction && msg.actionStatus === "pending" && conversationTransaction?.status === "funds_secured" && (
                  <div className="system-message-actions">
                    <Button radius="xl" color="gray" disabled={actionLoading} onClick={() => { setRefuseMessageId(msg._id); openRefuseModal(); }}>Refuser</Button>
                    <Button radius="xl" color="dark" disabled={actionLoading} loading={actionLoading} onClick={() => acceptBooking(msg._id)}>Accepter</Button>
                  </div>
                )}

                {!isSeller() && belongsToCurrentTransaction && msg.actionStatus === "pending" && conversationTransaction?.status === "validated" && (() => {
                  const locationEnded = offer?.contrat === "Location"
                    ? !!(msg.transaction?.endDate && new Date() >= new Date(msg.transaction.endDate))
                    : true;
                  return (
                    <div className="system-message-actions">
                      <Button
                        radius="xl"
                        fullWidth
                        color="dark"
                        disabled={!locationEnded || actionLoading}
                        loading={actionLoading}
                        onClick={() => confirmDelivery(msg._id, transactionId)}
                      >
                        {offer?.contrat === "Don" ? "Tout est OK" : "Confirmer la prestation"}
                      </Button>
                      {offer?.contrat === "Location" && (locationEnded ? (
                        <p className="txt11 thin" style={{ textAlign: "center", marginTop: 4, color: "#868e96" }}>
                          Ce sera automatiquement validé 72h après la fin de la location.
                        </p>
                      ) : (
                        <p className="txt11 thin" style={{ textAlign: "center", marginTop: 4 }}>
                          Disponible à partir du {new Date(msg.transaction.endDate).toLocaleDateString("fr-FR", { timeZone: "Europe/Paris" })}
                        </p>
                      ))}
                    </div>
                  );
                })()}

                </div>
              </div>
            );
          }

          return (
            <div key={`row-${msg._id}`}>
              {dateSeparator}
              <div className={isMine ? "message-row mine" : "message-row theirs"}>
                <div className="bubble">
                  <div>{msg.content}</div>
                  <span className="conversation-date">
                    {new Date(msg.createdAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
              </div>
            </div>
          );
        })}

    </div>
    {isOfferNoLongerAvailable() ? (
      <div style={{ padding: "8px 12px", borderTop: "1px solid #e9ecef", backgroundColor: "#fff" }}>
        <p className="txt13 thin txtCenter noMrgTB">Terminé. L'offre n'est plus disponible.</p>
      </div>
    ) : !isSeller() && !hasActiveTransaction() && offer?._id && (
      <div className="chat-cta-buy">
        <Button
          color="dark"
          radius="xl"
          loading={reservationLoading}
          onClick={async () => {
            if (wasLocationRefusedOrCancelled()) {
              // Créneau refusé ou annulé par le prestataire — renvoyer vers l'offre pour choisir de nouvelles disponibilités
              navigate(`/app/offers/offer/${offer._id}`);
              return;
            }

            const token = getToken();
            const startISO = convStartDate || preselectedStart;
            const endISO   = convEndDate   || preselectedEnd;

            if (offer?.contrat === "Location") {
              if (!startISO || !endISO) {
                // Dates non disponibles dans cette conv — renvoyer vers l'offre pour resélectionner
                navigate(`/app/offers/offer/${offer._id}`);
                return;
              }
              setReservationLoading(true);
              try {
                // Créer le lock 15 min
                const lockRes = await fetch(
                  `https://boldee-97d8d36af3bd.herokuapp.com/app/offers/book-temp/${offer._id}`,
                  {
                    method: "POST",
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ rangeDates: [startISO, endISO] })
                  }
                );
                const lockData = await lockRes.json();
                if (!lockRes.ok) { alert(lockData.message || "Créneau non disponible."); setReservationLoading(false); return; }

                // Lancer le checkout
                const nbDays = Math.max(1, Math.round((new Date(endISO) - new Date(startISO)) / 86400000) + 1);
                const checkoutRes = await fetch(
                  "https://boldee-97d8d36af3bd.herokuapp.com/app/payment/create-checkout-session",
                  {
                    method: "POST",
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                    credentials: "include",
                    body: JSON.stringify({
                      offerId: offer._id,
                      nbOfRentUnits: nbDays,
                      lockId: lockData.lock?.id,
                      startDate: startISO,
                      endDate: endISO
                    })
                  }
                );
                if (!checkoutRes.ok) { const errData = await checkoutRes.json(); alert(errData.message || "Erreur lors du paiement."); setReservationLoading(false); return; }
                const checkoutData = await checkoutRes.json();
                if (checkoutData.url) { window.location.href = checkoutData.url; return; }
                setReservationLoading(false);
              } catch (err) {
                console.error(err);
                setReservationLoading(false);
              }
            } else {
              // Vente / Don : ouvrir directement le paiement Stripe
              setReservationLoading(true);
              try {
                const checkoutRes = await fetch(
                  "https://boldee-97d8d36af3bd.herokuapp.com/app/payment/create-checkout-session",
                  {
                    method: "POST",
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                    credentials: "include",
                    body: JSON.stringify({
                      offerId: offer._id,
                      nbOfRentUnits: 1
                    })
                  }
                );
                if (!checkoutRes.ok) { const errData = await checkoutRes.json(); alert(errData.message || "Erreur lors du paiement."); setReservationLoading(false); return; }
                const checkoutData = await checkoutRes.json();
                if (checkoutData.url) { window.location.href = checkoutData.url; return; }
                setReservationLoading(false);
              } catch (err) {
                console.error(err);
                setReservationLoading(false);
              }
            }
          }}
        >
          {ctaLabel()}
        </Button>
      </div>
    )}
    <div className="chat-input-container">
      <input className="chat-input"
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder="Écrire un message..."
        onKeyDown={e => e.key === "Enter" && sendMessage()} />
        <button className="send-button" onClick={sendMessage} disabled={!text.trim()} >
          Envoyer
        </button>
    </div>
    </div>
  </div>
  );
};

export default ChatPanel;
